// client/src/App.jsx
import React, { useState, useEffect, lazy, Suspense, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import { Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./app.css";
import "./styles/darkMode.css";
import { UserProvider, UserContext } from "./components/UserContext";
import { CartProvider } from "./components/CartContext";
import { WishlistProvider } from "./components/WishlistContext";
import { FloatingCartProvider } from "./components/FloatingCartContext";
import { GlobalDiscountProvider } from "./components/GlobalDiscountContext";
import { DarkModeProvider } from "./components/DarkModeContext";
import ErrorBoundary from "./components/ErrorBoundary";
import AnnouncementBanner from "./components/AnnouncementBanner";
import DynamicSEO from "./components/DynamicSEO";
import MaintenanceMode from "./components/MaintenanceMode";
import StoreClosedBanner from "./components/StoreClosedBanner";
import FloatingCart from "./components/FloatingCart";
import TrustBanner from "./components/TrustBanner";
import GoogleAnalytics from "./components/GoogleAnalytics";
import CookieConsent from "./components/CookieConsent";
import SessionTimeoutProvider from "./components/SessionTimeoutProvider";
import { API_URL } from "./config";
import { usePageViewTracking } from "./hooks/usePageViewTracking";

// ============================================
// EAGER LOADED COMPONENTS (Critical for initial load)
// ============================================
import HomePageModern from "./pages/HomePageModern";
import HomeConcept3 from "./pages/HomeConcept3";
import Login from "./pages/Login";
import Register from "./pages/Register";

// ============================================
// LAZY LOADED COMPONENTS (Code Splitting)
// ============================================
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetailPage"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/checkout"));
const CheckoutSuccess = lazy(() => import("./pages/checkoutSuccess"));
const Cancel = lazy(() => import("./pages/Cancel"));
const AboutUs = lazy(() => import("./pages/aboutUs"));
const ContactUs = lazy(() => import("./pages/contactUs"));
const AboutConcept3 = lazy(() => import("./pages/AboutConcept3"));
const ContactConcept3 = lazy(() => import("./pages/ContactConcept3"));
const AboutConcept3Test = lazy(() => import("./pages/AboutConcept3Test"));
const ContactConcept3Test = lazy(() => import("./pages/ContactConcept3Test"));
const AccountPage = lazy(() => import("./pages/Account"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const OrderDetail = lazy(() => import("./pages/OrderDetails"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const ReturnsPolicy = lazy(() => import("./pages/ReturnsPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const RegisterTrade = lazy(() => import("./pages/RegisterTrade"));
const TrackRMA = lazy(() => import("./pages/TrackRMA"));
const NotFound = lazy(() => import("./pages/NotFound"));
const B2BResources = lazy(() => import("./pages/B2BResources"));
const QuoteView = lazy(() => import("./pages/QuoteView"));
const TradeAccountBenefits = lazy(() => import("./pages/TradeAccountBenefits"));
const CustomPricingQuote = lazy(() => import("./pages/CustomPricingQuote"));
const CommercialHVAC = lazy(() => import("./pages/CommercialHVAC"));
const DuctworkSupplies = lazy(() => import("./pages/DuctworkSupplies"));
const IndustrialVentilation = lazy(() => import("./pages/IndustrialVentilation"));
const FAQ = lazy(() => import("./pages/FAQ"));
const LocalFAQ = lazy(() => import("./pages/LocalFAQ"));

// ============================================
// LOADING FALLBACK COMPONENT
// ============================================
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    flexDirection: 'column',
    gap: '15px'
  }}>
    <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
    <p style={{ color: '#666', fontSize: '16px' }}>Loading...</p>
  </div>
);

// Site Settings Context Wrapper
function SiteSettingsWrapper({ children }) {
  const { user } = useContext(UserContext);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Track page views for analytics
  usePageViewTracking();

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/site-settings`);
        const data = await response.json();
        setSiteSettings(data);
      } catch (error) {
        console.error('Error fetching site settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSiteSettings();
    // Refresh settings every 30 seconds (increased frequency)
    const interval = setInterval(fetchSiteSettings, 30 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading) {
    return <PageLoader />;
  }

  // Check maintenance mode (admins can bypass)
  if (siteSettings?.maintenanceMode?.enabled && user?.role !== 'admin') {
    return <MaintenanceMode message={siteSettings.maintenanceMode.message} />;
  }

  return (
    <>
      {/* Show store closed banner if store is closed (but not in maintenance) */}
      {!siteSettings?.storeOpen && user?.role !== 'admin' && (
        <StoreClosedBanner message={siteSettings?.closedMessage} />
      )}
      {/* Admin indicator banner */}
      {user?.role === 'admin' && (siteSettings?.maintenanceMode?.enabled || !siteSettings?.storeOpen) && (
        <div style={{
          backgroundColor: '#17a2b8',
          color: 'white',
          padding: '8px 15px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          ⚠️ Admin Mode: You're bypassing{' '}
          {siteSettings?.maintenanceMode?.enabled && 'Maintenance Mode'}
          {siteSettings?.maintenanceMode?.enabled && !siteSettings?.storeOpen && ' and '}
          {!siteSettings?.storeOpen && 'Store Closed'}
          {' '}restrictions. Regular users cannot access the site.
        </div>
      )}
      {children}
    </>
  );
}

// Page transition wrapper component
function AnimatedRoutes() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage("fadeOut");
    }
  }, [location, displayLocation]);

  return (
    <div
      className={`page-transition ${transitionStage}`}
      onAnimationEnd={() => {
        if (transitionStage === "fadeOut") {
          setTransitionStage("fadeIn");
          setDisplayLocation(location);
        }
      }}
    >
      <Suspense fallback={<PageLoader />}>
        <Routes location={displayLocation}>
        <Route path="/success" element={<CheckoutSuccess />} />
        <Route path="/checkout-success" element={<CheckoutSuccess />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route path="/" element={<HomeConcept3 />} />
        <Route path="/home-concept-3" element={<HomeConcept3 />} />
        <Route path="/home-modern" element={<HomePageModern />} />
        <Route path="/products" element={<Products />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/about" element={<AboutConcept3 />} />
        <Route path="/contact" element={<ContactConcept3 />} />
        <Route path="/about-concept-3" element={<AboutConcept3 />} />
        <Route path="/contact-concept-3" element={<ContactConcept3 />} />
        <Route path="/about-concept-3-test" element={<AboutConcept3Test />} />
        <Route path="/contact-concept-3-test" element={<ContactConcept3Test />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/edit-profile" element={<EditProfile />} />
        <Route path="/order/:orderId" element={<OrderDetail />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/returns" element={<ReturnsPolicy />} />
        <Route path="/shipping" element={<ShippingPolicy />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/register-trade" element={<RegisterTrade />} />
        <Route path="/track-rma" element={<TrackRMA />} />
        <Route path="/b2b-resources" element={<B2BResources />} />
        <Route path="/b2b/quote/:quoteNumber" element={<QuoteView />} />
        <Route path="/trade-account-benefits" element={<TradeAccountBenefits />} />
        <Route path="/custom-pricing-quote" element={<CustomPricingQuote />} />
        <Route path="/commercial-hvac" element={<CommercialHVAC />} />
        <Route path="/ductwork-supplies" element={<DuctworkSupplies />} />
        <Route path="/industrial-ventilation" element={<IndustrialVentilation />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/local-faq" element={<LocalFAQ />} />
        {/* Catch-all route for 404 - must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <HelmetProvider>
          <DarkModeProvider>
            <UserProvider>
              <SessionTimeoutProvider>
                <CartProvider>
                  <FloatingCartProvider>
                    <WishlistProvider>
                      <GlobalDiscountProvider>
                      <SiteSettingsWrapper>
                      <DynamicSEO />
                      <GoogleAnalytics />
                      <CookieConsent />
                      <TrustBanner />
                      <AnnouncementBanner />
                      <FloatingCart />
                      <ScrollToTop />
                      <AnimatedRoutes />
                    </SiteSettingsWrapper>
                      </GlobalDiscountProvider>
                    </WishlistProvider>
                  </FloatingCartProvider>
                </CartProvider>
              </SessionTimeoutProvider>
            </UserProvider>
          </DarkModeProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
